import React, { useState, useEffect } from 'react';
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { CourseFormData, University, Campus } from '../../types/UniversityPortal';
import { universityPortalApi } from '../../services/universityPortalApi';

interface CourseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  course?: any; // For editing
}

const CourseModal: React.FC<CourseModalProps> = ({ isOpen, onClose, onSuccess, course }) => {
  const [formData, setFormData] = useState<CourseFormData>({
    universityId: 0,
    campusId: undefined,
    name: '',
    code: '',
    description: '',
    credits: undefined,
    durationMonths: undefined
  });
  const [isActive, setIsActive] = useState(true);

  // Update form data when course prop changes
  useEffect(() => {
    if (course) {
      setFormData({
        universityId: course.universityId || 0,
        campusId: course.campusId || undefined,
        name: course.name || '',
        code: course.code || '',
        description: course.description || '',
        credits: course.credits || undefined,
        durationMonths: course.durationMonths || undefined
      });
      // Set search query to show selected university
      if (course.universityName) {
        setSearchQuery(course.universityName);
      }
      // Set campus search query to show selected campus
      if (course.campusName) {
        setCampusSearchQuery(course.campusName);
      }
      setIsActive(course.isActive !== false);
    } else {
      setFormData({
        universityId: 0,
        campusId: undefined,
        name: '',
        code: '',
        description: '',
        credits: undefined,
        durationMonths: undefined
      });
      setSearchQuery('');
      setCampusSearchQuery('');
      setIsActive(true);
    }
  }, [course]);
  const [universities, setUniversities] = useState<University[]>([]);
  const [campuses, setCampuses] = useState<Campus[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [campusSearchQuery, setCampusSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [showCampusDropdown, setShowCampusDropdown] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadUniversities();
      loadCampuses();
    }
  }, [isOpen]);

  // Load campuses when university changes
  useEffect(() => {
    if (formData.universityId > 0) {
      loadCampusesForUniversity(formData.universityId);
    } else {
      setCampuses([]);
      setFormData(prev => ({ ...prev, campusId: undefined }));
    }
  }, [formData.universityId]);

  const loadUniversities = async () => {
    try {
      const data = await universityPortalApi.getUniversities();
      setUniversities(data);
    } catch (err) {
      console.error('Failed to load universities:', err);
    }
  };

  const loadCampuses = async () => {
    try {
      const data = await universityPortalApi.getCampuses();
      setCampuses(data);
    } catch (err) {
      console.error('Failed to load campuses:', err);
    }
  };

  const loadCampusesForUniversity = async (universityId: number) => {
    try {
      const data = await universityPortalApi.getCampuses();
      const universityCampuses = data.filter(campus => campus.universityId === universityId);
      setCampuses(universityCampuses);
    } catch (err) {
      console.error('Failed to load campuses for university:', err);
    }
  };

  const filteredUniversities = universities.filter(uni =>
    uni.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredCampuses = campuses.filter(campus =>
    campus.name.toLowerCase().includes(campusSearchQuery.toLowerCase())
  );

  const selectedUniversity = universities.find(uni => uni.id === formData.universityId);
  const selectedCampus = campuses.find(campus => campus.id === formData.campusId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const submitData = {
        ...formData,
        isActive: isActive
      };

      if (course) {
        await universityPortalApi.updateCourse(course.id, submitData);
      } else {
        await universityPortalApi.createCourse(submitData);
      }
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save course');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: (name === 'credits' || name === 'durationMonths') 
        ? (value ? parseInt(value) : undefined) 
        : value
    }));
  };

  const handleUniversitySelect = (university: University) => {
    setFormData(prev => ({ ...prev, universityId: university.id, campusId: undefined }));
    setSearchQuery(university.name);
    setCampusSearchQuery('');
    setShowDropdown(false);
  };

  const handleCampusSelect = (campus: Campus) => {
    setFormData(prev => ({ ...prev, campusId: campus.id }));
    setCampusSearchQuery(campus.name);
    setShowCampusDropdown(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">
              {course ? 'Edit Course' : 'Create New Course'}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* University Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                University *
              </label>
              <div className="relative">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setShowDropdown(true);
                    }}
                    onFocus={() => setShowDropdown(true)}
                    placeholder="Search universities..."
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                {showDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredUniversities.length > 0 ? (
                      filteredUniversities.map(university => (
                        <button
                          key={university.id}
                          type="button"
                          onClick={() => handleUniversitySelect(university)}
                          className="w-full px-4 py-2 text-left hover:bg-gray-50 focus:bg-gray-50"
                        >
                          {university.name}
                        </button>
                      ))
                    ) : (
                      <div className="px-4 py-2 text-gray-500">No universities found</div>
                    )}
                  </div>
                )}
              </div>
              {selectedUniversity && (
                <p className="mt-1 text-sm text-gray-600">
                  Selected: {selectedUniversity.name}
                </p>
              )}
            </div>

            {/* Campus Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Campus
              </label>
              <div className="relative">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={campusSearchQuery}
                    onChange={(e) => {
                      setCampusSearchQuery(e.target.value);
                      setShowCampusDropdown(true);
                    }}
                    onFocus={() => setShowCampusDropdown(true)}
                    placeholder="Search campuses..."
                    disabled={!formData.universityId}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                </div>
                
                {showCampusDropdown && formData.universityId && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredCampuses.length > 0 ? (
                      filteredCampuses.map(campus => (
                        <button
                          key={campus.id}
                          type="button"
                          onClick={() => handleCampusSelect(campus)}
                          className="w-full px-4 py-2 text-left hover:bg-gray-50 focus:bg-gray-50"
                        >
                          {campus.name} {campus.isMainCampus && '(Main Campus)'}
                        </button>
                      ))
                    ) : (
                      <div className="px-4 py-2 text-gray-500">No campuses found for this university</div>
                    )}
                  </div>
                )}
              </div>
              {selectedCampus && (
                <p className="mt-1 text-sm text-gray-600">
                  Selected: {selectedCampus.name} {selectedCampus.isMainCampus && '(Main Campus)'}
                </p>
              )}
              {!formData.universityId && (
                <p className="mt-1 text-sm text-gray-500">
                  Please select a university first
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Course Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., Computer Science, Business Administration"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Course Code
              </label>
              <input
                type="text"
                name="code"
                value={formData.code}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., CS101, MBA501"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Course description and objectives"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Credits
                </label>
                <input
                  type="number"
                  name="credits"
                  value={formData.credits || ''}
                  onChange={handleChange}
                  min="1"
                  max="200"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="120"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Duration (Months)
                </label>
                <input
                  type="number"
                  name="durationMonths"
                  value={formData.durationMonths || ''}
                  onChange={handleChange}
                  min="1"
                  max="120"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="48"
                />
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="isActive"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="isActive" className="ml-2 text-sm text-gray-700">
                Active
              </label>
            </div>

            {/* Actions */}
            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !formData.universityId}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Saving...' : (course ? 'Update' : 'Create')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CourseModal;
